import { useEffect, useState } from "react";
import type { ConfirmationAction } from "./types";
import styles from "./ConfirmationCard.module.css";

interface ConfirmationCardProps {
  title: string;
  body?: string;
  actions: ConfirmationAction[];
  onResolve: (action: ConfirmationAction) => void;
  resolved?: ConfirmationAction;
}

export function ConfirmationCard({
  title,
  body,
  actions,
  onResolve,
  resolved,
}: ConfirmationCardProps) {
  const [collapsed, setCollapsed] = useState(!!resolved);

  useEffect(() => {
    if (!resolved) return;
    // Let the flash pass, then collapse the card so the receipt stands alone.
    const t = setTimeout(() => setCollapsed(true), 360);
    return () => clearTimeout(t);
  }, [resolved]);

  useEffect(() => {
    if (resolved) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "1" || e.key === "2" || e.key === "3") {
        const action = actions.find((a) => a.key === e.key);
        if (action) {
          e.preventDefault();
          onResolve(action);
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [actions, resolved, onResolve]);

  if (collapsed) return null;

  const flashing = !!resolved;

  return (
    <div
      className={`${styles.card} ${flashing ? styles.flashing : ""}`}
      aria-hidden={flashing}
    >
      <div className={styles.header}>
        <span className={styles.glyph} aria-hidden>
          ⬡
        </span>
        <span className={styles.required}>action required</span>
        <span className={styles.title}>{title}</span>
      </div>
      {body && <div className={styles.body}>{body}</div>}
      <div className={styles.actions}>
        {actions.map((a) => (
          <button
            key={a.key}
            type="button"
            className={`${styles.action} ${styles[a.tone ?? "secondary"]} ${
              resolved?.key === a.key ? styles.chosen : ""
            }`}
            onClick={() => !resolved && onResolve(a)}
            disabled={!!resolved}
          >
            <span className={styles.keyChip}>[{a.key}]</span>
            <span className={styles.actionLabel}>{a.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
